/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */
import { IBoxModel } from "@adapptio/lib-shared";

export type TDragAndDropID = string;

export type TDroppableID = TDragAndDropID;

export type TDraggableID = TDragAndDropID;

export interface IDroppableProps {}

export interface IDragHandleProps {}

export interface IDraggableElProps {}

export enum DRAG_PHASE_TYPE {
	IDLE = "idle",
	PENDING = "pending",
	DRAGGING = "dragging"
}

export interface IDragPoint {
	x: number;
	y: number;
}

export interface IDragPhase {
	type: DRAG_PHASE_TYPE;
	draggableId: TDraggableID;
	dragging?: IDraggableInfo;
	draggingOver: TDroppableID;
	initialPoint: IDragPoint;
}

export interface IDragEventBinding<T extends keyof WindowEventMap = keyof WindowEventMap> {
	eventName: T;
	handler: (event: WindowEventMap[T]) => void;
}

export enum DND_STATE_PHASE {
	IDLE = "idle",
	PRE_DRAG = "pre_drag",
	DRAGGING = "dragging"
}

export interface IDraggableDimensions {
	width: number;
	height: number;
}

export interface IDragAndDropState {
	phase: DND_STATE_PHASE;
	isDragging: boolean;
	dimensions: Record<string, IDraggableDimensions>;
	draggingOver: TDroppableID;
}

export enum DND_ACTION {
	PREPARE_DRAG = "prepareDrag",
	DRAG = "drag"
}

interface IDragAndDropActionBase {
	type: DND_ACTION;
}

interface IDragAndDropActionPreDrag extends IDragAndDropActionBase {
	type: DND_ACTION.PREPARE_DRAG;
	draggableId: TDraggableID;
	point: IDragPoint;
}

export type TDragAndDropAction = IDragAndDropActionPreDrag;

export interface IDraggableRegistryEntry {
	id: string;
	index: number;
	droppableId: TDroppableID;
	getInfo: () => IDraggableInfo;
	onDragMove: (data: IDragMoveEventData) => void;
	onDragDrop: () => void;
}

export interface IDraggableRegistryApi {
	register: (entry: IDraggableRegistryEntry) => void;
	unregister: (id: TDraggableID) => void;
	exists: (id: TDraggableID) => boolean;
	findById: (id: TDraggableID) => IDraggableRegistryEntry | null;
}

export interface IDroppableRegistryEntry {
	id: string;
	onDragMove: (data: IDragMoveEventData) => void;
	onDragDrop: () => void;
}

export interface IDroppableRegistryApi {
	register: (entry: IDroppableRegistryEntry) => void;
	unregister: (id: TDroppableID) => void;
	exists: (id: TDroppableID) => boolean;
	findById: (id: TDroppableID) => IDroppableRegistryEntry | null;
}

export interface IDragAndDropRegistry {
	draggable: IDraggableRegistryApi;
	droppable: IDroppableRegistryApi;
	getDraggablesOfDroppable: (droppableId: TDroppableID) => IDraggableRegistryEntry[];
	emitDragMove: (data: IDragMoveEventData) => void;
	emitDrop: () => void;
	clean: () => void;
}

export interface IDragAndDropRegistryEntries {
	draggables: Record<TDraggableID, IDraggableRegistryEntry>;
	droppables: Record<TDroppableID, IDroppableRegistryEntry>;
}

export interface IDraggableInfo {
	draggableId: TDraggableID;
	tagName: string;
	box: IBoxModel;
	display: React.CSSProperties["display"];
}

export interface IDragMoveEventData {
	dragging: IDraggableInfo;
	initialPoint: IDragPoint;
	currentPoint: IDragPoint;
	draggingOver: TDroppableID;
}

export interface IDragEndData {
	destination: {
		droppableId: TDroppableID;
		index: number;
	};
	source: {
		droppableId: TDroppableID;
		draggableId: TDraggableID;
		index: number;
	};
}
